import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Form, message, Modal, notification } from 'antd';

import { useDefaultFormErrorNotification, useDisclosure } from '@/hooks';

import {
  useCreateWorkOrder,
  useWorkOrderCreateByPorto,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';

import { ModalProps } from '@/types/ModalProps';
import WorkOrderForm, { WorkOrderFormData } from '../../WorkOrderForm';
import {
  CreateWorkOrderRequest,
  WorkOrderCreateByPolicyCompanyCode,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { AxiosError } from 'axios';
import { DateTimeFormat } from '@/utils/moment';
import CustomerVehicleSimplifiedRegisterModal from '@/components/Customers/CustomerVehicleSimplifiedRegisterModal';
import { useState } from 'react';
import { CustomerReduced } from '@/core/domain/people/Customers/Customers.types';
import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { useQueryClient } from 'react-query';
import { removeMask } from '@/utils/mask';

function CreateWorkOrderModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const history = useHistory();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<WorkOrderFormData>();
  const customerIdField = Form.useWatch('customerId', form);

  const createWorkOrder = useCreateWorkOrder();
  const workOrderCreateByPolicy = useWorkOrderCreateByPorto();

  const [customer, setCustomer] = useState<CustomerReduced>();

  const [customerVehicle, setCustomerVehicle] =
    useState<CustomerVehicleReduced>();

  const [isFormLoading, setIsFormLoading] = useState(false);

  const [vehicleModalOpen, vehicleModalAction] = useDisclosure(false);

  const queryClient = useQueryClient();

  const handleVehicleModalFinish = async (vehicle: CustomerVehicleReduced) => {
    await queryClient.invalidateQueries([
      'customers/vehicles/filter',
      customerIdField,
    ]);
    form.setFieldsValue({ vehicleId: vehicle.id });
    form.setFieldValue('licensePlate', vehicle.licensePlate);

    setCustomerVehicle(vehicle);
  };

  const handleCancel = () => {
    setCustomerVehicle(undefined);
    setCustomer(undefined);
    handleVisible();
  };

  const handleOk = async () => {
    try {
      const { policy, voucher, hasVoucher, ...values } =
        await form.validateFields();

      if (!voucher?.customer && values.customerId === 0) {
        form.setFields([
          {
            name: 'customerId',
            value: undefined,
            errors: ['Informe um cliente válido'],
          },
        ]);

        return message.error('Valide os campos!');
      }

      const obj: CreateWorkOrderRequest = {
        ...values,
        customerId: values.vehicleId ? undefined : values.customerId,
        vehicleId: values.vehicleId || undefined,
        entryDate: values.entryDate
          ? values.entryDate.format(DateTimeFormat)
          : undefined,
        estimatedLeaveDate: values.estimatedLeaveDate
          ? values.estimatedLeaveDate.format(DateTimeFormat)
          : undefined,
        lastOilChange: values.lastOilChange
          ? values.lastOilChange.format(DateTimeFormat)
          : undefined,
      };

      let workOrderId;
      let complementId;

      if (policy && policy?.companyCode) {
        const result = await workOrderCreateByPolicy.mutateAsync({
          ...values,
          vehicleId: values.vehicleId || undefined,
          customerId: values.customerId || undefined,
          companyCode:
            `domain.enums.company_code.${policy.companyCode.toLowerCase()}` as WorkOrderCreateByPolicyCompanyCode,
          entryDate: values.entryDate
            ? values.entryDate.format(DateTimeFormat)
            : undefined,
          policyNumber: policy.policyNumber as string,
          customer: policy.customer,
          vehicle: {
            ...policy.vehicle,
            licensePlate: values.licensePlate,
            prisma: values.prisma,
            currentKilometers: values.currentKilometers,
            tankLevel: values.tankLevel,
          },
          lastOilChange: values.lastOilChange
            ? values.lastOilChange.format(DateTimeFormat)
            : undefined,
        });

        workOrderId = result.workOrderId;
        complementId = result.workOrderComplementId;
      } else if (hasVoucher && !policy?.companyCode) {
        const voucherCustomer = voucher?.customer;
        const voucherAddress = voucher?.address;

        const { phone, email, ...restCustomer } = { ...voucherCustomer };

        const result = await workOrderCreateByPolicy.mutateAsync({
          ...values,
          customerId: values.customerId !== 0 ? values.customerId : undefined,
          entryDate: values.entryDate
            ? values.entryDate.format(DateTimeFormat)
            : undefined,
          customer:
            values.customerId === 0
              ? {
                  ...restCustomer,
                  contacts: {
                    email: email as string,
                    phone: removeMask(phone),
                  },
                  address: voucherAddress
                    ? {
                        ...voucherAddress,
                        cep: removeMask(voucherAddress?.cep),
                      }
                    : undefined,
                }
              : undefined,
          vehicle: values.vehicleId
            ? {
                licensePlate: values.licensePlate,
                prisma: values.prisma,
                currentKilometers: values.currentKilometers,
                tankLevel: values.tankLevel,
              }
            : undefined,
          lastOilChange: values.lastOilChange
            ? values.lastOilChange.format(DateTimeFormat)
            : undefined,
          hasPortoVoucher: true,
        });

        workOrderId = result.workOrderId;
        complementId = result.workOrderComplementId;
      } else {
        const result = await createWorkOrder.mutateAsync(obj);

        workOrderId = result.id;
        complementId = result.complements[0].id;

        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.create.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'workOrders.create.notifications.success.description',
          }),
        });
      }

      history.push(
        `/app/movements/workOrders/${workOrderId}/complements/${complementId}/generalInfo`,
      );

      handleCancel();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  return (
    <>
      <Modal
        title={intl.formatMessage({ id: 'workOrders.create' })}
        open={visible}
        onOk={handleOk}
        okText="Gerar orçamento"
        okButtonProps={{
          disabled: isFormLoading,
          id: 'workorder-create-submit',
        }}
        onCancel={handleCancel}
        width={992}
        confirmLoading={
          createWorkOrder.isLoading || workOrderCreateByPolicy.isLoading
        }
        destroyOnClose
      >
        <WorkOrderForm
          form={form}
          setCustomer={setCustomer}
          customer={customer}
          vehicleToggle={vehicleModalAction.toggle}
          customerVehicle={customerVehicle}
          setCustomerVehicle={setCustomerVehicle}
          setIsFormLoading={setIsFormLoading}
        />
      </Modal>

      <CustomerVehicleSimplifiedRegisterModal
        visible={vehicleModalOpen}
        handleVisible={vehicleModalAction.close}
        onFinish={handleVehicleModalFinish}
        customerId={customer?.id}
        vehicleId={customerVehicle?.id}
      />
    </>
  );
}

export default CreateWorkOrderModal;
